import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import PageHeader, { DarkPage } from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserPlus, Copy, X, Users, Mail, ShieldCheck, Zap, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const ROLE_BADGE = {
  owner: "bg-[#00F5D4]/10 text-[#00F5D4]",
  admin: "bg-[#F59E0B]/10 text-[#F59E0B]",
  member: "bg-[#374151] text-[#9CA3AF]",
  viewer: "bg-[#1F2937] text-[#6B7280]",
};

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin — manage team & all projects" },
  { value: "member", label: "Member — create & edit projects" },
  { value: "viewer", label: "Viewer — read-only" },
];

const PLAN_LABEL = { free: "Free", starter: "Starter", professional: "Professional", enterprise: "Enterprise" };

const Team = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [seats, setSeats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openInvite, setOpenInvite] = useState(false);
  const [form, setForm] = useState({ email: "", role: "member" });
  const [busy, setBusy] = useState(false);

  const canManage = user?.role === "owner" || user?.role === "admin" || user?.role === "superadmin";
  const atCapacity = seats && seats.limit != null && seats.remaining === 0;
  const overLimit = seats && seats.over_limit;

  const refresh = async () => {
    setLoading(true);
    try {
      const [membersRes, seatsRes] = await Promise.all([
        api.get("/team/members"),
        api.get("/team/seats").catch(() => ({ data: null })),
      ]);
      setMembers(membersRes.data);
      setSeats(seatsRes.data);
      if (canManage) {
        const invRes = await api.get("/team/invitations").catch(() => ({ data: [] }));
        setInvitations(invRes.data);
      }
    } catch (e) {
      toast.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage]);

  const sendInvite = async () => {
    if (!form.email.trim()) return;
    setBusy(true);
    try {
      await api.post("/team/invitations", { email: form.email.trim(), role: form.role });
      toast.success(`Invitation sent to ${form.email}`);
      setForm({ email: "", role: "member" });
      setOpenInvite(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send invitation");
    } finally {
      setBusy(false);
    }
  };

  const copyInviteLink = (token) => {
    const url = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Invite link copied to clipboard"),
      () => toast.error("Could not copy — please copy manually")
    );
  };

  const revokeInvite = async (invitationId) => {
    try {
      await api.delete(`/team/invitations/${invitationId}`);
      toast.success("Invitation revoked");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke");
    }
  };

  const changeRole = async (memberUserId, role) => {
    try {
      await api.patch(`/team/members/${memberUserId}`, { role });
      toast.success("Role updated");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update role");
    }
  };

  const removeMember = async (memberUserId, name) => {
    if (!window.confirm(`Remove ${name} from this workspace? They'll keep their account but lose access to all projects, deals and meetings here.`)) return;
    try {
      await api.delete(`/team/members/${memberUserId}`);
      toast.success(`${name} removed`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove member");
    }
  };

  const pendingInvites = invitations.filter((i) => i.status === "pending");

  return (
    <DarkPage>
      <PageHeader
        eyebrow="Workspace"
        title="Team & Access"
        description="Invite people into this workspace, manage their roles, and audit pending invitations."
        actions={
          canManage && (
            <Button
              onClick={() => setOpenInvite(true)}
              className="rounded-none bg-[#00F5D4] hover:bg-[#00D4B8] text-[#030712] font-semibold"
              data-testid="open-invite-dialog-btn"
            >
              <UserPlus className="w-4 h-4 mr-1" /> Invite member
            </Button>
          )
        }
      />

      {/* Plan & seat usage strip */}
      {seats && (
        <div
          className={`px-5 sm:px-8 lg:px-12 py-3 border-b border-[#1F2937] flex flex-wrap items-center gap-x-6 gap-y-2 text-xs ${
            overLimit ? "bg-[#FF007A]/5" : atCapacity ? "bg-[#F59E0B]/5" : "bg-[#111827]"
          }`}
          data-testid="seat-usage-strip"
        >
          <div className="flex items-center gap-2">
            <Zap className={`w-3.5 h-3.5 ${overLimit ? "text-[#FF007A]" : atCapacity ? "text-[#F59E0B]" : "text-[#00F5D4]"}`} strokeWidth={1.7} />
            <span className="uppercase tracking-[0.2em] text-[#6B7280]">Plan</span>
            <span className="font-display font-bold uppercase text-white" data-testid="seat-plan">{PLAN_LABEL[seats.plan] || seats.plan}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="uppercase tracking-[0.2em] text-[#6B7280]">Seats</span>
            <span className="font-mono text-white" data-testid="seat-count">
              {seats.used} / {seats.limit == null ? "∞" : seats.limit}
            </span>
            {seats.pending_invites > 0 && (
              <span className="text-[#6B7280]">({seats.pending_invites} pending)</span>
            )}
          </div>
          {overLimit && (
            <span className="text-[#FF007A]" data-testid="over-limit-message">
              You&apos;re over your plan&apos;s seat allowance. Existing members keep access, but you can&apos;t add new ones until you upgrade or remove someone.
            </span>
          )}
          {(atCapacity || overLimit) && canManage && (
            <Link
              to="/pricing"
              className={`ml-auto inline-flex items-center gap-1 font-semibold ${overLimit ? "text-[#FF007A] hover:text-[#FF007A]/80" : "text-[#F59E0B] hover:text-[#F59E0B]/80"}`}
              data-testid="upgrade-plan-link"
            >
              {overLimit ? "Upgrade plan" : "Upgrade plan to invite more"} <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
      )}

      <div className="grid grid-cols-12 gap-0 min-h-[70vh]">
        {/* Members panel */}
        <section className="col-span-12 lg:col-span-8 border-r border-[#1F2937]">
          <div className="px-5 sm:px-8 lg:px-12 py-6 border-b border-[#1F2937] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-4 h-4 text-[#00F5D4]" strokeWidth={1.5} />
              <h2 className="font-display font-bold text-lg tracking-tight">Members</h2>
            </div>
            <span className="text-[11px] uppercase tracking-[0.25em] text-[#4B5563] font-mono" data-testid="member-count">
              {members.length} {members.length === 1 ? "person" : "people"}
            </span>
          </div>

          {loading ? (
            <div className="px-5 sm:px-8 lg:px-12 py-12 text-sm text-[#6B7280]">Loading…</div>
          ) : (
            <div className="divide-y divide-[#1F2937]">
              {members.map((m) => {
                const isOwner = m.role === "owner";
                const isSelf = m.user_id === user?.user_id;
                return (
                  <div
                    key={m.user_id}
                    className="px-5 sm:px-8 lg:px-12 py-4 flex items-center gap-4 hover:bg-[#111827] transition-colors"
                    data-testid={`member-row-${m.user_id}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#1F2937] text-white grid place-items-center text-sm font-semibold overflow-hidden flex-shrink-0">
                      {m.picture ? (
                        <img src={m.picture} alt={m.name} className="w-full h-full object-cover" />
                      ) : (
                        (m.name?.[0] || "?").toUpperCase()
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {m.name}
                        {isSelf && <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">You</span>}
                      </div>
                      <div className="text-xs text-[#6B7280] truncate">{m.email}</div>
                    </div>
                    <span
                      className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${ROLE_BADGE[m.role] || ROLE_BADGE.viewer}`}
                      data-testid={`member-role-${m.user_id}`}
                    >
                      {m.role}
                    </span>
                    {canManage && !isOwner && !isSelf && (
                      <div className="flex items-center gap-1">
                        <select
                          value={m.role}
                          onChange={(e) => changeRole(m.user_id, e.target.value)}
                          className="h-9 border border-[#374151] px-2 text-xs bg-[#030712] text-white focus:outline-none focus:ring-2 focus:ring-[#00F5D4]"
                          data-testid={`role-select-${m.user_id}`}
                        >
                          <option value="admin">Admin</option>
                          <option value="member">Member</option>
                          <option value="viewer">Viewer</option>
                        </select>
                        <Button
                          onClick={() => removeMember(m.user_id, m.name)}
                          variant="ghost"
                          size="sm"
                          className="rounded-none text-[#6B7280] hover:text-[#FF007A]"
                          data-testid={`remove-member-${m.user_id}`}
                          title="Remove from workspace"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Pending invitations */}
        <aside className="col-span-12 lg:col-span-4 bg-[#111827]">
          <div className="px-6 lg:px-8 py-6 border-b border-[#1F2937] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-[#00F5D4]" strokeWidth={1.5} />
              <h2 className="font-display font-bold text-lg tracking-tight">Pending invites</h2>
            </div>
            <span className="text-[11px] uppercase tracking-[0.25em] text-[#4B5563] font-mono" data-testid="pending-invite-count">
              {pendingInvites.length}
            </span>
          </div>

          {!canManage ? (
            <div className="px-6 lg:px-8 py-8 text-xs text-[#6B7280] flex items-start gap-2">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              Only owners and admins can manage invitations.
            </div>
          ) : pendingInvites.length === 0 ? (
            <div className="px-6 lg:px-8 py-8 text-sm text-[#6B7280]" data-testid="no-pending-invites">
              No pending invitations.
            </div>
          ) : (
            <div className="divide-y divide-[#1F2937]">
              {pendingInvites.map((inv) => (
                <div key={inv.invitation_id} className="px-6 lg:px-8 py-4" data-testid={`invite-${inv.invitation_id}`}>
                  <div className="text-sm font-medium truncate">{inv.email}</div>
                  <div className="text-[11px] text-[#6B7280] mt-0.5">
                    {inv.role} · expires {new Date(inv.expires_at).toLocaleDateString()}
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    <Button
                      onClick={() => copyInviteLink(inv.token)}
                      variant="outline"
                      size="sm"
                      className="rounded-none text-xs h-8 border-[#374151] bg-transparent text-[#9CA3AF] hover:text-white hover:border-[#6B7280]"
                      data-testid={`copy-invite-${inv.invitation_id}`}
                    >
                      <Copy className="w-3 h-3 mr-1" /> Copy link
                    </Button>
                    <Button
                      onClick={() => revokeInvite(inv.invitation_id)}
                      variant="ghost"
                      size="sm"
                      className="rounded-none text-xs h-8 text-[#6B7280] hover:text-[#FF007A]"
                      data-testid={`revoke-invite-${inv.invitation_id}`}
                    >
                      <X className="w-3 h-3 mr-1" /> Revoke
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </aside>
      </div>

      {/* Invite dialog */}
      <Dialog open={openInvite} onOpenChange={setOpenInvite}>
        <DialogContent className="rounded-none bg-[#111827] border-[#374151] text-white sm:max-w-md" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-white">Invite to workspace</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Email address</Label>
              <Input
                type="email"
                className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white placeholder:text-[#4B5563] focus:ring-[#00F5D4]"
                placeholder="teammate@company.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Role</Label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full rounded-none mt-1 h-10 border border-[#374151] bg-[#030712] text-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#00F5D4]"
                data-testid="invite-role-select"
              >
                {ROLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="text-[11px] text-[#6B7280] bg-[#030712] border border-[#374151] px-3 py-2">
              We&apos;ll generate a shareable invite link. Send it to your teammate — once they sign in (or create an account)
              with this email and open the link, they join your workspace instantly.
            </div>
            {atCapacity && (
              <div
                className="text-[11px] text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30 px-3 py-2 flex items-start gap-2"
                data-testid="invite-capacity-warning"
              >
                <Zap className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.7} />
                <span>
                  You&apos;re out of seats on the {PLAN_LABEL[seats.plan]} plan.{" "}
                  <Link to="/pricing" className="underline font-semibold">Upgrade</Link> to invite more teammates.
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={sendInvite}
              disabled={busy || !form.email.trim() || atCapacity}
              className="rounded-none bg-[#00F5D4] hover:bg-[#00D4B8] text-[#030712] font-semibold"
              data-testid="send-invite-btn"
            >
              {busy ? "Sending…" : "Create invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DarkPage>
  );
};

export default Team;
